import React, {useCallback, useState} from "react";
import {MailOutlined} from "@ant-design/icons";
import {useResetPasswordMutation} from "../services/nestjs-base-api";
import {Button, Form, Input, message, theme, Typography} from "antd";
import {FormattedMessage, useIntl} from "react-intl";
import AuthLayout from "../components/AuthLayout";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

const { useToken } = theme;
const { Text, Link } = Typography;

const StyledForm = styled(Form)`
  .ant-form-item {
    margin-bottom: 24px;
  }
  
  .ant-input-affix-wrapper,
  .ant-input {
    height: 48px;
    font-size: 16px;
    border-radius: 8px;
    border: 1px solid #d9d9d9;
    
    &:hover,
    &:focus {
      border-color: #4169e1;
      box-shadow: 0 0 0 2px rgba(65, 105, 225, 0.1);
    }
  }
  
  .ant-input-prefix {
    color: #4169e1;
    font-size: 18px;
    margin-right: 12px;
  }
`;

const StyledButton = styled(Button)`
  height: 48px;
  font-size: 16px;
  font-weight: 500;
  border-radius: 8px;
  background: linear-gradient(135deg, #4169e1 0%, #1890ff 100%);
  border: none;
  box-shadow: 0 4px 12px rgba(65, 105, 225, 0.3);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(65, 105, 225, 0.4);
  }
  
  &:disabled {
    background: #f5f5f5;
    box-shadow: none;
    transform: none;
  }
`;

const FooterWrapper = styled.div`
  margin-top: 24px;
  text-align: center;
`;

const StyledLink = styled(Link)`
  color: #4169e1;
  font-weight: 500;
  
  &:hover {
    color: #1890ff;
  }
`;

const ForgotPage: React.FunctionComponent = () => {
    const intl = useIntl();
    const { token } = useToken();
    const navigate = useNavigate();
    const [resetPassword, resetPasswordResult] = useResetPasswordMutation();
    const [messageApi, contextHolder] = message.useMessage();
    const [hideForm, setHideForm] = useState<boolean>(false);

    const handleResetPassword = useCallback(async (values: any) => {
        await resetPassword({email: values.email});
        setHideForm(true);
    }, [resetPassword]);

    return (
        <>
            {contextHolder}
            <AuthLayout
                title={hideForm 
                    ? intl.formatMessage({ id: 'forgot.success.title' }) 
                    : intl.formatMessage({ id: 'forgot.title' })
                }
                subtitle={hideForm 
                    ? intl.formatMessage({ id: 'forgot.success.welcome' }) 
                    : intl.formatMessage({ id: 'forgot.welcome' })
                }
            >
                <div style={{display: hideForm ? 'none' : 'block'}}>
                    <StyledForm
                        name="normal_forgot"
                        onFinish={handleResetPassword}
                        layout="vertical"
                        requiredMark="optional"
                    >
                        <Form.Item
                            name="email"
                            rules={[
                                {
                                    type: "email",
                                    required: true,
                                    message: intl.formatMessage({ id: 'forgot.form.email.missing' }),
                                },
                            ]}
                        >
                            <Input
                                prefix={<MailOutlined/>}
                                placeholder={intl.formatMessage({ id: 'forgot.form.email' })}
                            />
                        </Form.Item>
                        <Form.Item>
                            <StyledButton 
                                block={true} 
                                type="primary" 
                                htmlType="submit"
                                disabled={resetPasswordResult.isLoading}
                                loading={resetPasswordResult.isLoading}
                            >
                                <FormattedMessage id='forgot.form.submit' />
                            </StyledButton>
                            <FooterWrapper>
                                <Text style={{ color: token.colorTextSecondary }}>
                                    <FormattedMessage id='forgot.remembered' />
                                </Text>{" "}
                                <StyledLink onClick={() => navigate('/sign-in')}>
                                    <FormattedMessage id='forgot.signIn' />
                                </StyledLink>
                            </FooterWrapper>
                        </Form.Item>
                    </StyledForm>
                </div>
                <div style={{display: hideForm ? 'block' : 'none'}}>
                    {/* Success message is handled by the title/subtitle in AuthLayout */}
                </div>
            </AuthLayout>
        </>
    );
}

export default ForgotPage;